import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, Download, ShieldCheck, UserCircle } from "lucide-react";
import { openInstallPrompt } from "./InstallPrompt";
import ProfileDialog from "./ProfileDialog";
import { siteConfig } from "../lib/siteConfig";
import VerifiedName from "./VerifiedName";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user && user !== false && user.role === "admin";
  const isAuthed = user && user !== false && !isAdmin;
  const [profileOpen, setProfileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-[#030705]/70 backdrop-blur-2xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-10 h-16 sm:h-20 flex items-center justify-between gap-2">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <img src={siteConfig.logoUrl} alt={siteConfig.name} className="h-9 w-9 rounded-full object-cover ring-1 ring-[#D4AF37]/60 sm:h-10 sm:w-10" />
          <span className="hidden min-[360px]:inline font-display text-lg tracking-tight sm:text-xl">{siteConfig.name}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          {[
            ["Games", "/games"],
            ["Leaderboard", "/leaderboard"],
          ].map(([label, to]) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-link-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `transition-colors ${isActive ? "text-[#D4AF37]" : "text-white/70 hover:text-white"}`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
          <button
            onClick={openInstallPrompt}
            data-testid="nav-install-app"
            title={`Install ${siteConfig.name}`}
            className="hidden sm:inline-flex min-h-[44px] items-center gap-2 rounded-full border border-[#D4AF37]/60 bg-[#D4AF37] px-5 py-2.5 text-sm font-extrabold text-black shadow-lg shadow-[#D4AF37]/20 transition-all hover:-translate-y-0.5 hover:bg-[#F1C40F]"
          >
            <Download className="h-4 w-4" /> Download now
          </button>

          {isAdmin ? (
            <>
              <Link
                to="/admin/dashboard"
                data-testid="nav-admin-console"
                className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/35 bg-white/[0.04] px-3 py-2 text-sm font-semibold text-white/85 transition-colors hover:bg-white/[0.08] hover:text-white"
              >
                <ShieldCheck className="h-4 w-4 text-[#D4AF37]" /> Admin console
              </Link>
              <button onClick={() => { logout(); nav("/"); }} data-testid="nav-logout" title="Sign out"
                className="p-2.5 rounded-full hover:bg-white/5 border border-transparent hover:border-white/10">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : isAuthed ? (
            <>
              <button
                type="button"
                onClick={() => setProfileOpen(true)}
                data-testid="nav-profile-mobile"
                title="My profile"
                aria-label="My profile"
                className="grid h-10 w-10 place-items-center overflow-hidden rounded-full border border-[#D4AF37]/40 bg-white/[0.04] text-[#D4AF37] transition-colors hover:bg-white/[0.08] sm:hidden"
              >
                {user.profile_image_url ? (
                  <img src={user.profile_image_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <UserCircle className="h-5 w-5" />
                )}
              </button>
              <button
                type="button"
                onClick={() => setProfileOpen(true)}
                data-testid="nav-profile"
                className="hidden sm:flex items-center gap-2.5 pl-2 pr-4 py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 transition-colors"
              >
                {user.profile_image_url ? (
                  <img src={user.profile_image_url} alt="" className="w-9 h-9 rounded-full object-cover ring-1 ring-[#D4AF37]/50" />
                ) : (
                  <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8a6a14] grid place-items-center font-display font-black text-black text-base">
                    {user.username.charAt(0).toUpperCase()}
                  </span>
                )}
                <VerifiedName user={user} name={user.username} className="max-w-40 text-base font-semibold" />
              </button>
              <button onClick={() => { logout(); nav("/"); }} data-testid="nav-logout" title="Sign out"
                className="p-2.5 rounded-full hover:bg-white/5 border border-transparent hover:border-white/10">
                <LogOut className="w-4 h-4" />
              </button>
              <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="px-2 py-2 text-sm text-white/80 hover:text-white sm:px-3">Sign in</Link>
              <Link to="/signup" data-testid="nav-signup" className="rounded-full bg-[#D4AF37] px-3 py-2 text-sm font-semibold text-black transition-all hover:-translate-y-0.5 hover:bg-[#F1C40F] sm:px-5">
                Create account
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
